import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Copy, FileText, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { OutlineButton, PrimaryButton } from "@/components/masters/UI";

/**
 * TextReportDialog
 * Displays the published allocation sign-off text report.
 * Automatically copies to clipboard on open, and selects the text for
 * manual copy if the clipboard API is unavailable.
 */
export const TextReportDialog = ({ open, onOpenChange, text, title = "Allocation text report" }) => {
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  const handleCopy = useCallback(async () => {
    if (!text) return;
    let didCopy = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        didCopy = true;
      }
    } catch {
      didCopy = false;
    }

    if (didCopy) {
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 2500);
    } else {
      // If the clipboard is unavailable, select the text so it can be copied by hand.
      setCopied(false);
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
      }
      toast.info("Please copy the selected text (Ctrl+C / Cmd+C)");
    }
  }, [text]);

  useEffect(() => {
    if (open && text) {
      // Attempt automatic copy on preview open
      handleCopy();
    } else {
      setCopied(false);
    }
  }, [open, text, handleCopy]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl rounded-2xl bg-white p-6 shadow-2xl"
        data-testid="text-report-dialog"
      >
        <DialogHeader className="mb-2">
          <div className="flex items-center gap-2">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{
                backgroundColor: "rgba(0, 53, 158, 0.08)",
                color: "var(--vision-navy)",
              }}
            >
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="font-display text-lg font-bold text-vision-ink">
                {title}
              </DialogTitle>
              <p className="text-xs text-vision-slate/60">
                Sign-off summary grouped by Job Role and Department
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="relative my-2">
          <textarea
            ref={textareaRef}
            readOnly
            value={text || ""}
            rows={14}
            data-testid="text-report-preview"
            className="w-full resize-none rounded-xl border bg-slate-50 p-4 font-mono text-xs leading-relaxed text-vision-ink outline-none selection:bg-[color:var(--vision-orange)] selection:text-white"
            style={{ borderColor: "rgba(52, 73, 94, 0.15)" }}
            onClick={(e) => e.target.select()}
          />
        </div>

        <DialogFooter className="mt-2 flex items-center justify-between gap-2 sm:justify-between">
          <div className="text-xs text-vision-slate/60">
            {copied ? (
              <span className="font-semibold text-emerald-600">
                ✓ Copied to clipboard
              </span>
            ) : (
              <span>Click text to select all</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <OutlineButton
              testId="text-report-close"
              onClick={() => onOpenChange(false)}
            >
              Close
            </OutlineButton>
            <PrimaryButton
              testId="text-report-copy-btn"
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <Check className="mr-1 inline h-4 w-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="mr-1 inline h-4 w-4" />
                  Copy
                </>
              )}
            </PrimaryButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TextReportDialog;
